// ============================================================================
// LA SAISIE D'UN COEFFICIENT CONTRACTUEL — le champ et ses deux conversions, partagés.
//
// UTILITÉ : les Référentiels (fournisseurs + marques) et l'onglet « Coefficients fournisseur » de
// Prix & marges saisissent LA MÊME clause. Extraits VERBATIM de `referentiels.tsx` à l'arrivée du
// second consommateur (règle de 2) : deux copies de « 2,50 ↔ 25 000 points de base » finiraient par
// diverger d'un arrondi, et la clause écrite ne serait plus celle affichée.
// ============================================================================
import { useEffect, useState } from 'react';
import { showToast } from '@orgazone/ui-web';
import { parseMinor } from '@orgazone/money-format';
import { t } from '../i18n';

/** Le coefficient à l'écran : 25 000 points de base se lisent « 2,50 ». */
export const coefAffiche = (bp: number | null): string => (bp === null ? '' : (bp / 10_000).toFixed(2).replace('.', ','));

/**
 * Le coefficient tapé, en points de base — `undefined` si la saisie n'est pas un nombre exploitable.
 * ⚠️ La chaîne VIDE vaut `null` : c'est ainsi qu'on RETIRE une clause, et pour une marque qu'on la
 * remet en héritage. Sans ce cas, une clause posée par erreur serait impossible à enlever.
 */
export function coeffInput(v: string): number | null | undefined {
  if (v.trim() === '') return null;
  // ⚠️ QUATRE DÉCIMALES AU PLUS, REFUSÉES au-delà : un coefficient se stocke en points de base, et « 2,55555 » arrondi
  //    en silence deviendrait une clause que personne n'a tapée. `parseMinor` lit aussi la virgule et le point.
  const lu = parseMinor(v, { scale: 4 });
  return lu.state === 'ok' && lu.minor > 0 ? lu.minor : undefined;
}

/**
 * Dit qu'une clause vient d'être écrite — la même phrase partout où un coefficient se saisit.
 * ⚠️ LE SUCCÈS SE DIT, COMME L'ÉCHEC (Forme X) : la case s'écrit à la sortie du champ, sans bouton — un écran qui ne
 *    parle qu'en cas de refus apprend que le silence veut dire « rien ne s'est passé ».
 */
export function annoncerCoefficient(label: string, bp: number | null): void {
  showToast(bp === null
    ? t('Clause de {v} retirée.', { v: label })
    : t('Coefficient de {v} enregistré : ×{c}.', { v: label, c: coefAffiche(bp) }), 'ok');
}

/**
 * Une case de coefficient — elle n'écrit qu'à la SORTIE du champ.
 * ⚠️ PAS À CHAQUE FRAPPE : « 2,5 » est un état transitoire de « 2,50 », et écrire à la volée
 * enverrait au journal une clause que personne n'a voulue, avant de l'écraser par la suivante.
 */
export function ChampCoef({ valeur, placeholder, heading, nom, largeur, hauteur, taille, couleur, onValidate }: {
  valeur: string;
  placeholder: string;
  heading: string;
  /**
   * Le nom du champ quand il se répète dans une liste — « Coefficient de JACK » : la bulle (`heading`) est la même
   * sur chaque ligne, et une aide technique annoncerait trente champs identiques. Seul, le champ prend la bulle.
   */
  nom?: string;
  largeur: number;
  hauteur: number;
  taille: string;
  couleur: string;
  /**
   * Ce que la sortie du champ décide. Rendre une promesse de `false` dit « rien n'a été écrit » (refus du serveur,
   * saisie illisible) : la case reprend alors la valeur ENREGISTRÉE.
   */
  onValidate: (v: string) => void | Promise<boolean>;
}) {
  const [saisie, setSaisie] = useState(valeur);
  // La valeur du serveur reprend la main dès qu'elle change : c'est lui qui a le dernier mot.
  useEffect(() => { setSaisie(valeur); }, [valeur]);

  return (
    <input
      value={saisie}
      onChange={(e) => setSaisie(e.target.value)}
      onBlur={() => {
        const ecrit = onValidate(saisie);
        // ⚠️ UNE SAISIE NON ÉCRITE REPREND LA VALEUR DU SERVEUR : refusée, la relecture ne change pas `valeur` (rien n'a
        //    été écrit), donc l'effet ci-dessus ne se déclenche pas — la case garderait le chiffre refusé, et se lirait
        //    « enregistré » une fois le message parti.
        if (ecrit) void ecrit.then((ok) => { if (!ok) setSaisie(valeur); });
      }}
      onKeyDown={(e) => { if (e.key === 'Enter') e.currentTarget.blur(); }}
      placeholder={placeholder}
      title={heading}
      aria-label={nom ?? heading}
      inputMode="decimal"
      style={{
        width: largeur, height: hauteur, padding: hauteur > 24 ? '0 8px' : '0 5px',
        boxSizing: 'border-box', border: '1px solid var(--ink-200)', borderRadius: 'var(--r-xs)',
        background: 'var(--paper)', color: couleur, fontSize: taille, fontWeight: hauteur > 24 ? 600 : 700,
        textAlign: 'right', outline: 'none', fontVariantNumeric: 'tabular-nums',
      }}
    />
  );
}
